import * as RadixProgress from '@radix-ui/react-progress';
import { memo } from 'react';
import { useInView } from 'react-intersection-observer';

import { InfoIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

const MAX_PERCENT = 100;
const STORAGE_WARNING_PERCENT = 70;
const DEFAULT_INFO_LABEL = 'Please note that this total may take a few minutes to update.';

export type MeterProps = {
  hasInfo?: boolean;
  hasPercentage?: boolean;
  infoLabel?: string;
  isOutOfStorage?: boolean;
  label?: string;
  maxBytes?: string;
  usedStoragePercent: number;
  usedBytes: string;
  note?: string;
};

export const Meter = memo(
  ({
    hasInfo,
    hasPercentage,
    infoLabel = DEFAULT_INFO_LABEL,
    isOutOfStorage,
    label,
    maxBytes,
    note,
    usedBytes,
    usedStoragePercent,
  }: MeterProps) => {
    const isOverStorageLimit = isOutOfStorage ?? usedStoragePercent > MAX_PERCENT;
    const percentage = Math.min(usedStoragePercent, MAX_PERCENT);
    const [ref, inView] = useInView({ triggerOnce: true });

    return (
      <div className="flex flex-col gap-2" data-testid="PLAN_USAGE_METER">
        {(!!label || hasInfo) && (
          <div className="flex items-center">
            {!!label && <span className="ads:text-metadata text-14 text-gray-9">{label}</span>}
            {hasInfo && (
              <div
                className={cn('flex items-center gap-2 ads:text-secondary text-gray-11', !!label && 'ml-auto')}
                data-testid="PLAN_USAGE_METER_INFO"
              >
                <div className="text-14 tabular-nums">
                  <span
                    className="font-semibold ads:text-primary text-gray-12"
                    data-testid="PLAN_USAGE_METER_INFO_STORAGE_USED"
                  >
                    {usedBytes}
                  </span>
                  {!!maxBytes && (
                    <>
                      <span className="mx-1 ads:text-metadata text-gray-10">used of</span>
                      <span
                        className="ads:text-metadata text-gray-10"
                        data-testid="PLAN_USAGE_METER_INFO_WORKSPACE_STORAGE"
                      >
                        {maxBytes}
                      </span>
                    </>
                  )}
                  {hasPercentage && (
                    <span className="ml-1 ads:text-metadata text-gray-10">({usedStoragePercent}%)</span>
                  )}
                </div>
                <Tooltip className="max-w-[200px] sm:max-w-full" label={infoLabel}>
                  <button
                    type="button"
                    aria-label="Storage info"
                    data-testid="PLAN_USAGE_METER_INFO_BUTTON"
                    className="flex items-center text-gray-11"
                  >
                    <InfoIcon className="size-4" />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        )}
        <RadixProgress.Root
          aria-label={label ?? 'Storage usage'}
          className="h-2 w-full overflow-hidden rounded-full ads:bg-input bg-gray-4 will-change-transform"
          value={percentage}
        >
          <RadixProgress.Indicator
            ref={ref}
            className={cn('h-full transition-[width] duration-[660ms] ease-[cubic-bezier(0.65,0,0.35,1)]', {
              'ads:bg-error bg-red-8': isOverStorageLimit,
              'ads:bg-ads-amber-9 bg-yellow-7': !isOverStorageLimit && percentage >= STORAGE_WARNING_PERCENT,
              'ads:bg-ads-sky-5 bg-sky-5': !isOverStorageLimit && percentage < STORAGE_WARNING_PERCENT,
            })}
            style={{ width: inView ? `${percentage}%` : '0%' }}
          />
        </RadixProgress.Root>
        {!!note && (
          <p className="ads:text-metadata text-12 text-gray-9" data-testid="PLAN_USAGE_METER_NOTE">
            {note}
          </p>
        )}
      </div>
    );
  },
);

Meter.displayName = 'Meter';
